import { useState, useCallback, useEffect } from 'react';
import { updateDirection } from '@/i18n';
import { useTranslation } from 'react-i18next';

import { useChangeDefaultLanguageMutation } from '@/entities/user';
import { selectAvailableLanguages, selectCurrentLanguage, setLanguage } from '@/entities/language';

import { useAppDispatch, useAppSelector, useFormWithValidation } from '@/shared/lib';

import { createPreferencesSchema, type PreferencesFormValues } from './validation';

interface UsePreferencesFormProps {
  open: boolean;
  onSuccess?: () => void;
}

export const usePreferencesForm = ({ open, onSuccess }: UsePreferencesFormProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [changeLanguage, { isLoading }] = useChangeDefaultLanguageMutation();
  const [alertState, setAlertState] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const availableLanguages = useAppSelector(selectAvailableLanguages);
  const currentLanguage = useAppSelector(selectCurrentLanguage);

  const preferencesSchema = createPreferencesSchema(t);
  const form = useFormWithValidation(preferencesSchema, {
    languageCode: currentLanguage
  });

  const onSubmit = useCallback(
    async (data: PreferencesFormValues) => {
      if (data.languageCode !== currentLanguage) {
        setAlertState(null);
        try {
          await changeLanguage({ languageCode: data.languageCode }).unwrap();
          i18n.changeLanguage(data.languageCode);
          dispatch(setLanguage(data.languageCode));
          updateDirection(data.languageCode);
          setAlertState({
            type: 'success',
            message: t('preferences.saved', 'Preferences saved successfully')
          });
          onSuccess?.();
        } catch (_error) {
          setAlertState({
            type: 'error',
            message: t('preferences.error', 'Failed to save language preferences')
          });
        }
      }
    },
    [changeLanguage, currentLanguage, dispatch, i18n, t, onSuccess]
  );

  useEffect(() => {
    if (open) {
      form.reset({ languageCode: currentLanguage });
      setAlertState(null);
    }
  }, [open, currentLanguage, form]);

  return {
    form,
    onSubmit,
    alertState,
    isLoading,
    availableLanguages,
    currentLanguage
  };
};
